﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import { NotificationType, sendNotification } from '@tk/utilities/tk.notification';

export default class KDMZPosTerminalList extends TKCustomElementFactory {
    constructor() {
        super();
    }

    connectedCallback(): void {
        this.registerClickListener();

        const parent = this.parentElement?.closest('[data-visibility-id]') ?? this;

        if (parent && !parent.hasAttribute('hidden')) {
            this.restoreCounterValues();
        }
    }

    registerClickListener(): void {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.onClick.bind(this),
        });
    }

    onClick(event: Event): void {
        const target = event.target as Element;

        const decreaser = target.closest('[data-tk-counter-decreaser]');
        const increaser = target.closest('[data-tk-counter-increaser]');

        if (!decreaser && !increaser) {
            return;
        }

        const parent = (decreaser || increaser)?.parentElement;
        const input = parent?.querySelector<HTMLInputElement>('input[type="number"]');

        if (!input) {
            return;
        }

        if (decreaser) {
            input.stepDown();

            if (input.valueAsNumber === 0) {
                decreaser.setAttribute('disabled', '');
            }
        }

        if (increaser) {
            input.stepUp();

            const decreaserButton = parent?.querySelector<HTMLButtonElement>('[data-tk-counter-decreaser]');

            if (decreaserButton && input.valueAsNumber > 0) {
                decreaserButton.removeAttribute('disabled');
            }
        }

        this.updateCounterValues();

        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    private updateCounterValues(): void {
        const values: Record<string, string> = {};
        const activeTerminalTypes = new Set<string>();

        this.querySelectorAll<HTMLElement>('[data-tk-counter]').forEach((counter) => {
            const key = counter.dataset.tkCounter;

            if (!key) {
                return;
            }

            const input = counter.querySelector<HTMLInputElement>('input[type="number"]');

            if (!input) {
                return;
            }

            const value = input.valueAsNumber || 0;

            values[key] = value.toString();

            if (value > 0) {
                const wrapper = counter.closest<HTMLElement>('[data-terminal-type]');

                const terminalType = wrapper?.dataset.terminalType;

                if (terminalType) {
                    activeTerminalTypes.add(terminalType);
                }
            }
        });

        const output = this.querySelector<HTMLInputElement>('[data-tk-counter-values]');

        if (output) {
            output.value = JSON.stringify(values);
        }

        const infoMsg = this.querySelector<HTMLElement>('.tk-message');

        if (infoMsg) {
            infoMsg.hidden = activeTerminalTypes.size < 2;
        }
    }

    private restoreCounterValues(): void {
        const output = this.querySelector<HTMLInputElement>('[data-tk-counter-values]');

        if (!output?.value) {
            return;
        }

        // String in gültiges JSON umwandeln.
        // Beispiel:
        // {'693225':'0','693226':'1'}
        // ->
        // {"693225":"0","693226":"1"}
        const jsonString = output.value.replace(/'/g, '"');

        let values: Record<string, string>;

        try {
            values = JSON.parse(jsonString);
        } catch (error) {
            console.error('Invalid counter values:', output.value, error);
            return;
        }

        const activeTerminalTypes = new Set<string>();

        Object.entries(values).forEach(([key, value]) => {
            const counter = this.querySelector<HTMLElement>(`[data-tk-counter="${CSS.escape(key)}"]`);

            if (!counter) {
                return;
            }

            const input = counter.querySelector<HTMLInputElement>('input[type="number"]');

            if (!input) {
                return;
            }

            input.value = String(value);

            const numericValue = input.valueAsNumber || 0;

            const decreaserButton = counter.querySelector<HTMLButtonElement>('[data-tk-counter-decreaser]');

            if (decreaserButton) {
                if (numericValue === 0) {
                    decreaserButton.setAttribute('disabled', '');
                } else {
                    decreaserButton.removeAttribute('disabled');
                }
            }

            if (numericValue > 0) {
                const wrapper = counter.closest<HTMLElement>('[data-terminal-type]');

                const terminalType = wrapper?.dataset.terminalType;

                if (terminalType) {
                    activeTerminalTypes.add(terminalType);
                }
            }
        });

        const infoMsg = this.querySelector<HTMLElement>('.tk-message');

        if (infoMsg) {
            infoMsg.hidden = activeTerminalTypes.size < 2;
        }
    }

    handleSuccessResponse(response): void {
        if (response && response.success) {
            const html = response.dataAsHtml;

            if (html.trim() !== '') {
                this.innerHTML = html;
            }
        }
    }

    handleErrorResponse(error): void {
        console.error(error);
    }
}

/*
Damit gibt es jetzt drei Situationen, in denen die gespeicherten Werte wiederhergestellt werden:
    - Beim connectedCallback(), wenn kdmz-posterminal-list bereits sichtbar ist.
    - Beim Entfernen von hidden durch den MutationObserver.
    - Nach handleSuccessResponse(), wenn dort das HTML ersetzt wurde und kdmz-posterminal-list sichtbar ist.
Ein wichtiger Punkt dabei: restoreCounterValues() ruft am Ende updateCounterValues() auf. Dadurch werden nach dem Wiederherstellen auch .tk-message und das JSON wieder konsistent aktualisiert.
Wenn posterminal-list nach handleSuccessResponse() zunächst hidden ist und erst kurz danach sichtbar wird, greift anschließend der MutationObserver und stellt die Werte dann wieder her.
*/
